/**
 * MergeRoutesModal — pick a target route on the same date, confirm the merge.
 * Chauffeur defaults to the target's chauffeur, user can override.
 */

import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useCalendarStore } from '../../stores/calendar-store';
import type { RouteListItem } from '../../lib/salesforce';

interface MergeRoutesModalProps {
  sourceRoute: RouteListItem;
  onClose: () => void;
  onMerged?: (targetRouteId: string) => void;
}

export function MergeRoutesModal({ sourceRoute, onClose, onMerged }: MergeRoutesModalProps) {
  const { routesByDate, chauffeurs, mergeRoutes, isMerging } = useCalendarStore();

  const candidates = useMemo(() => {
    const sameDay = routesByDate[sourceRoute.datum] ?? [];
    return sameDay.filter((r) => r.id !== sourceRoute.id && !r.locked);
  }, [routesByDate, sourceRoute.id, sourceRoute.datum]);

  const [targetId, setTargetId] = useState<string | null>(null);
  const [chauffeurId, setChauffeurId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Preselect chauffeur when target changes
  useEffect(() => {
    if (!targetId) {
      setChauffeurId('');
      return;
    }
    const target = candidates.find((r) => r.id === targetId);
    setChauffeurId(target?.chauffeurId ?? '');
  }, [targetId, candidates]);

  const target = candidates.find((r) => r.id === targetId) ?? null;
  const canSubmit = !!target && !isMerging;

  const handleSubmit = async () => {
    if (!target) return;
    setError(null);
    try {
      await mergeRoutes(sourceRoute.id, target.id, chauffeurId || undefined);
      onMerged?.(target.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Samenvoegen mislukt');
    }
  };

  const displayDate = new Date(sourceRoute.datum + 'T00:00:00').toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="text-lg font-semibold text-gray-900">
            Samenvoegen: {sourceRoute.routeName || sourceRoute.name}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="text-gray-400 hover:text-gray-600"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
            </svg>
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4 capitalize">{displayDate}</p>

        {candidates.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg">
            Geen andere routes op deze dag om mee samen te voegen.
          </div>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                1. Kies doelroute
              </p>
              <div className="space-y-2 max-h-72 overflow-auto pr-1">
                {candidates.map((route) => {
                  const isSelected = route.id === targetId;
                  return (
                    <label
                      key={route.id}
                      className={clsx(
                        'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      )}
                    >
                      <input
                        type="radio"
                        name="merge-target"
                        value={route.id}
                        checked={isSelected}
                        onChange={() => setTargetId(route.id)}
                        className="mt-1 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {route.routeName || route.name}
                          </span>
                          <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                            {route.stopCount} stops
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span className="truncate">
                            {route.chauffeurName ?? 'Geen chauffeur'}
                          </span>
                          <span>↑ {route.stopCountOphalen}</span>
                          <span>↓ {route.stopCountAfleveren}</span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 uppercase mb-2">
                2. Chauffeur op samengevoegde route
              </label>
              <select
                value={chauffeurId}
                onChange={(e) => setChauffeurId(e.target.value)}
                disabled={!target}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white disabled:bg-gray-50"
              >
                <option value="">Geen chauffeur</option>
                {chauffeurs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {target && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed">
                Je verplaatst <strong>{sourceRoute.stopCount} stops</strong> van{' '}
                <strong>{sourceRoute.routeName || sourceRoute.name}</strong> naar{' '}
                <strong>{target.routeName || target.name}</strong>. De route{' '}
                <strong>{sourceRoute.routeName || sourceRoute.name}</strong> wordt verwijderd.
                Je kunt dit binnen 60 seconden ongedaan maken.
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
          </>
        )}

        <div className="flex gap-3 justify-end mt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isMerging}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isMerging ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Samenvoegen…
              </>
            ) : (
              'Routes samenvoegen'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
